<template>
  <div class="aufgaben-dateien mt-4">
    <div v-for="datei in dateien" :key="datei.id" class="aufgaben-dateien__item">
      <div class="d-flex align-items-center" style="min-width: 0">
        <component
          :is="getFileIcon(datei.dateiname)"
          :size="16"
          class="mr-2 flex-shrink-0"
          style="color: #3699ff"
        />
        <span class="aufgaben-dateien__name" @click="downloadDatei(datei)">
          {{ datei.dateiname }}
        </span>
        <span class="aufgaben-dateien__size ml-2">({{ formatFileSize(datei.groesse) }})</span>
      </div>
      <div class="d-flex align-items-center" style="gap: 8px">
        <span class="aufgaben-dateien__action" @click="downloadDatei(datei)">
          <Download :size="16" />
        </span>
        <span class="aufgaben-dateien__action aufgaben-dateien__action--delete" @click="deleteDatei(datei)">
          <Trash2 :size="16" />
        </span>
      </div>
    </div>

    <!-- Leeres Array, weil die datei direkt hochgeladen werden -->
    <DateiUpload :value="[]" :existing-file-names="existingFileNames" @input="uploadDateien" />

    <DeleteConfirmationModal
      id="modalDateiLoeschen"
      title="Datei löschen?"
      entityBezeichnung="die Datei"
      :label="pendingDeleteDatei?.dateiname"
      @confirm="confirmDeleteDatei"
    />
  </div>
</template>

<script>
import axios from 'axios';
import apiService from '@/core/common/services/api.service';
import {
  formatFileSize,
  getFileIcon,
  uploadDatei,
  triggerBrowserDownload,
} from '@/core/common/helpers/datei.utils';
import DateiUpload from '@/components/common/datei-upload.vue';
import DeleteConfirmationModal from '@/components/common/delete-confirmation-modal.vue';
import { FileText, Image as ImageIcon, FileSpreadsheet, File, Download, Trash2 } from 'lucide-vue';

export default {
  components: {
    FileText,
    ImageIcon,
    FileSpreadsheet,
    File,
    Download,
    Trash2,
    DateiUpload,
    DeleteConfirmationModal,
  },
  props: {
    aufgabeId: {
      type: Number,
      required: true,
    },
    dateien: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      pendingDeleteDatei: null,
    };
  },
  computed: {
    // Liefert die Dateinamen aller bereits hochgeladenen Anhänge der Aufgabe
    // Dient der Duplikats-Prüfung in der DateiUpload-Komponente, damit identische Namen nicht erneut hochgeladen werden
    existingFileNames() {
      return this.dateien.map(datei => datei.dateiname);
    },
  },
  methods: {
    formatFileSize,
    getFileIcon,
    async uploadDateien(files) {
      for (const file of files) {
        const response = await uploadDatei(this.aufgabeId, file);
        this.$emit('uploaded', response.data.result);
      }
    },
    // Hier wird axios direkt statt apiService verwendet, weil apiService.get() keine zusätzlichen
    // Konfigurationsoptionen wie { responseType: 'blob' } unterstützt. Für Datei-Downloads muss
    // responseType auf 'blob' gesetzt werden, damit die Antwort als Binärdaten (z.B. PDF, Bild)
    // und nicht als JSON interpretiert wird.
    async downloadDatei(datei) {
      const response = await axios.get(`/aufgaben/dateien/${datei.id}/download`, { responseType: 'blob' });
      triggerBrowserDownload(new Blob([response.data]), datei.dateiname);
    },
    deleteDatei(datei) {
      this.pendingDeleteDatei = datei;
      this.$bvModal.show('modalDateiLoeschen');
    },
    async confirmDeleteDatei() {
      const datei = this.pendingDeleteDatei;
      this.$bvModal.hide('modalDateiLoeschen');
      if (!datei) return;
      await apiService.delete(`/aufgaben/dateien/${datei.id}`);
      this.$emit('deleted', datei.id);
      this.pendingDeleteDatei = null;
    },
  },
};
</script>

<style scoped>
.aufgaben-dateien__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  margin-bottom: 6px;
}

.aufgaben-dateien__item:hover {
  border-color: var(--primary);
}

.aufgaben-dateien__name {
  font-size: 13px;
  color: var(--text-body);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.aufgaben-dateien__size {
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
}

.aufgaben-dateien__action {
  flex-shrink: 0;
  color: #6b7280;
  cursor: pointer;
}

.aufgaben-dateien__action:hover,
.aufgaben-dateien__name:hover {
  color: var(--primary);
}

.aufgaben-dateien__action--delete:hover {
  color: var(--danger);
}
</style>
